// Vanilla CSS for the tab primitives (SelectionTab, SelectionTabPill,
// SelectionTabFill). Prefix: tab-. Aggregated into libraryStyles.
// "Playful Frost": underline tabs use the teal accent; pill + fill tabs sit in a
// frosted strip tray with a solid teal selected pill + soft teal glow.
export const tabStyles = `
  /* SelectionTab — underline */
  .tab-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem 1rem;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
    text-align: center;
  }
  .tab-item {
    border-bottom-width: 2px;
    border-bottom-style: solid;
    transition: all var(--duration-base) var(--ease-standard);
    padding: 0.5rem 1rem;
    cursor: pointer;
  }
  .tab-text-selected { color: var(--catchup-blue); }
  .tab-text { color: var(--text-muted); }
  .tab-item:hover .tab-text { color: var(--text-secondary); }
  .tab-border-selected { border-bottom-color: var(--catchup-blue); }
  .tab-border { border-bottom-color: var(--catchup-blue-100); }
  .tab-title { font-size: var(--fs-lg); }
  .tab-subtitle { font-size: var(--fs-md); }

  /* SelectionTabPill — frosted pills */
  .tab-pill-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
  .tab-pill {
    padding: 0.5rem 1rem;
    border-radius: var(--rad-catchup-full);
    border: 1px solid transparent;
    transition: all var(--duration-base) var(--ease-standard);
    cursor: pointer;
  }
  .tab-pill-selected {
    background-color: var(--catchup-blue);
    color: var(--catchup-white);
    box-shadow: 0 2px 8px rgba(87, 194, 211, 0.35);
  }
  .tab-pill-unselected {
    /* Solid (no blur) — one pill per unselected tab, tab strips render on
       scrolling screens; blur only frosts the flat page bg. */
    background-color: var(--glass-flat-solid);
    border-color: var(--glass-flat-border);
    color: var(--catchup-blue-800);
  }
  .tab-pill-unselected:hover {
    background-color: var(--glass-active-bg);
    border-color: var(--glass-active-border);
    color: var(--brand-dark);
  }
  .tab-pill-label { font-size: var(--fs-base); font-weight: var(--font-weight-semi); }

  /* SelectionTabFill — frosted segmented tray */
  .tab-fill-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    justify-content: center;
    text-align: center;
    padding: var(--space-1);
    border-radius: var(--rad-catchup-full);
    background: var(--glass-strip-bg);
    -webkit-backdrop-filter: var(--glass-strip-filter);
    backdrop-filter: var(--glass-strip-filter);
    border: 1px solid var(--glass-strip-border);
    box-shadow: var(--glass-strip-shadow);
  }
  .tab-fill-item { cursor: pointer; }
  .tab-fill-text {
    transition: all var(--duration-base) var(--ease-standard);
    border-radius: var(--rad-catchup-full);
    padding: 0.35rem 0.85rem;
  }
  .tab-fill-text-selected {
    color: var(--catchup-white);
    background-color: var(--catchup-blue-500);
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.5), 0 2px 8px rgba(87, 194, 211, 0.35);
  }
  .tab-fill-text-unselected { color: var(--text-muted); }

  /* MorphTabs — frosted strip tray whose active indicator slides + briefly
     expands between tabs (faithful port of the design DS pf-tabs--morph). */
  .tab-mt {
    position: relative;
    display: inline-flex;
    /* Content-sized even when used as a flex/grid item (where inline-flex would
       otherwise be blockified + stretched to full width). */
    width: fit-content;
    gap: var(--space-1);
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -ms-overflow-style: none;
    scrollbar-width: none;
    font-family: var(--font-sans);
    /* This strip is itself a horizontal scroll container (overflow-x: auto above) so
       a long tab row can be dragged — a backdrop-filter therefore re-blurred the
       whole strip on every scroll frame. --glass-flat-solid is the token minted for
       exactly this ("near-opaque, so it reads as frosted glass WITHOUT a
       backdrop-filter"), so the strip keeps its frost with nothing to re-sample. */
    background: var(--glass-flat-solid);
    border: 1px solid var(--glass-strip-border);
    box-shadow: var(--glass-strip-shadow);
    padding: var(--space-1);
    border-radius: var(--rad-catchup-full);
  }
  .tab-mt::-webkit-scrollbar { display: none; }
  .tab-mt-btn {
    position: relative;
    z-index: 1;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: var(--fs-base);
    font-weight: var(--font-weight-semi);
    color: var(--text-muted);
    padding: 8px var(--space-4);
    border-radius: var(--rad-catchup-full);
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    white-space: nowrap;
    transition: all var(--duration-base) var(--ease-standard);
  }
  .tab-mt-btn:hover { color: var(--text-secondary); }
  .tab-mt-btn.is-active { color: var(--catchup-white); }
  .tab-mt-count {
    font-size: var(--fs-sm);
    font-weight: var(--font-weight-bold);
    background: rgba(0, 0, 0, 0.10);
    padding: 1px 6px;
    border-radius: var(--rad-catchup-full);
  }
  .tab-mt-btn.is-active .tab-mt-count { background: rgba(255, 255, 255, 0.28); }
  .tab-mt__glow {
    position: absolute;
    z-index: 0;
    top: 0;
    bottom: 0;
    pointer-events: none;
    border-radius: var(--rad-catchup-full);
    background: linear-gradient(90deg, var(--catchup-blue-300), var(--catchup-blue-500));
    filter: blur(13px);
    opacity: 0.45;
    transition: left var(--duration-slow) var(--ease-glass), width var(--duration-slow) var(--ease-glass);
  }
  .tab-mt__ind {
    position: absolute;
    z-index: 0;
    top: var(--space-1);
    bottom: var(--space-1);
    pointer-events: none;
    border-radius: var(--rad-catchup-full);
    background: linear-gradient(90deg, var(--catchup-blue-400), var(--catchup-blue-500));
    box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.5), 0 4px 12px rgba(87, 194, 211, 0.4);
    transition: left var(--duration-slow) var(--ease-glass), width var(--duration-slow) var(--ease-glass),
      transform var(--duration-base) var(--ease-glass), border-radius var(--duration-base) var(--ease-glass);
  }
  .tab-mt__ind.is-morphing { transform: scaleY(1.08); border-radius: var(--rad-catchup-large); }
  @media (prefers-reduced-motion: reduce) {
    .tab-mt__ind, .tab-mt__glow { transition: none; }
    .tab-mt__ind.is-morphing { transform: none; }
  }
`;
